// hooks/useViewingRequests.ts
// Viewing requests - currently demo mode only
// TODO: Add API endpoints for viewing requests

import { useState, useEffect, useCallback } from 'react';
import { trackEvent } from '../lib/api';

interface ViewingRequest {
  id: string;
  unit_id: string | null;
  building_id: string;
  requested_date: string;
  requested_time: string;
  name: string;
  email: string;
  phone: string | null;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface ViewingRequestFormData {
  unit_id: string;
  building_id: string;
  preferred_date: string;
  preferred_time: string;
  name: string;
  email: string;
  phone?: string;
  message?: string;
}

type ViewingStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';

interface UseViewingRequestsOptions {
  buildingId: string;
  status?: ViewingStatus;
}

interface UseViewingRequestsResult {
  requests: ViewingRequest[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface CreateViewingRequestResult {
  success: boolean;
  error: Error | null;
  requestId: string | null;
}

// Hook for fetching viewing requests (admin)
export function useViewingRequests({
  buildingId,
  status
}: UseViewingRequestsOptions): UseViewingRequestsResult {
  const [requests, setRequests] = useState<ViewingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Demo mode - return empty array
    // TODO: Add API endpoint GET /api/viewing-requests/:buildingId
    setRequests([]);
    setLoading(false);
  }, [buildingId, status]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return { requests, loading, error, refetch: fetchRequests };
}

// Function to create a viewing request (public)
export async function createViewingRequest(
  data: ViewingRequestFormData
): Promise<CreateViewingRequestResult> {
  console.log('[Demo] Creating viewing request:', data);

  // Track analytics
  await trackEvent('viewing_request', data.building_id, data.unit_id);

  return {
    success: true,
    error: null,
    requestId: `demo-${Date.now()}`
  };
}

// Function to update viewing request status (admin)
export async function updateViewingStatus(
  requestId: string,
  status: ViewingStatus,
  notes?: string
): Promise<{ success: boolean; error: Error | null }> {
  console.log('[Demo] Updating viewing status:', { requestId, status, notes });
  return { success: true, error: null };
}
